import type { DifficultyBand, PuzzleArchetype, PlacementStrategyId } from '../../model/recipe';

/**
 * Versioned generation tuning.
 *
 * Every knob that shapes campaign content lives here, keyed by content version. Changing a
 * value in `TUNING_V1` would silently rewrite released levels, so new tuning must be added
 * as a new version and referenced by `LevelIdentity.contentVersion`.
 */

export interface BandConfig {
  readonly band: DifficultyBand;
  readonly minWidth: number;
  readonly maxWidth: number;
  readonly minHeight: number;
  readonly maxHeight: number;
  /**
   * Target share of active stencil cells covered by arrows, in permille.
   *
   * These sit at the measured saturation point for each band's board size. Saturation falls
   * as boards grow — a longer corridor is harder to keep clear, so fewer of the remaining
   * cells can host a legal head — from ~75% on a 12x16 board to ~53% on a 36x49 one.
   */
  readonly occupancyPermille: readonly [number, number];
  /**
   * Target legal-move counts as a share of the arrow count. Expressing these as ratios
   * rather than absolute counts keeps the same intent on a 14-arrow board and a 300-arrow
   * board: what matters is how much of the board is open at once.
   */
  readonly initialMoveRatioPermille: readonly [number, number];
  readonly averageMoveRatioPermille: readonly [number, number];
  /** Inclusive range of arrow path lengths in cells; never below two. */
  readonly arrowLengthRange: readonly [number, number];
  /** Weight for a tail continuing straight versus turning; higher turn weight = mazier. */
  readonly straightBias: number;
  readonly turnBias: number;
  /**
   * How strongly paths are drawn across existing exit corridors.
   *
   * This is the lever on dependency depth, and the only one that works: openness has a
   * structural floor (within a row, the outermost arrow per direction is always legal), so
   * pushing bias from 0 to 64 moves the open ratio only 0.47 -> 0.44 while depth climbs
   * 5.8 -> 8.6. Depth is what makes the order of play matter.
   */
  readonly blockingBias: readonly [number, number];
  readonly forcedStepRatioPermille: number;
  readonly dependencyDepthRange: readonly [number, number];
  readonly candidateBudget: number;
  readonly maxPlacementAttempts: number;
  readonly maxRepairIterations: number;
  readonly archetypes: readonly PuzzleArchetype[];
  readonly strategies: readonly PlacementStrategyId[];
  readonly stencilCategories: readonly string[];
  readonly maximumStencilComplexity: number;
  /**
   * Minimum silhouette solidity. Thin silhouettes (letters, spirals, outlines) scatter arrows
   * across separate rows and columns, so almost nothing blocks anything; solid ones pack them
   * together and produce the entanglement the puzzle depends on. See stencil/solidity.ts.
   */
  readonly minimumStencilSolidity: number;
}

export interface HardConstraints {
  readonly minInitialMoves: number;
  readonly maxInitialMoves: number;
  /** Cap on how much of the board may be legal at once, as a share of the arrow count. */
  readonly maxInitialMoveRatioPermille: number;
  readonly minArrowCount: number;
  /** Minimum cells an acceptable board must cover; the real measure of board substance. */
  readonly minOccupiedCells: number;
  readonly minDependencyDepth: number;
  readonly maxForcedStepRatioPermille: number;
  /** Reject candidates whose occupancy misses the target by more than this, in permille. */
  readonly maxOccupancyDeviationPermille: number;
  readonly minSilhouetteCoverage: number;
  readonly maxRepetitionPenalty: number;
  readonly maxDirectionalImbalance: number;
}

export interface ScoringWeights {
  readonly occupancy: number;
  readonly initialMoves: number;
  readonly meanMoves: number;
  readonly forcedRatio: number;
  readonly dependencyDepth: number;
  readonly dispersion: number;
  readonly coverage: number;
  readonly repetition: number;
  readonly directionBalance: number;
  readonly unlockRhythm: number;
  readonly arrowLength: number;
}

export interface TuningConfig {
  readonly contentVersion: number;
  readonly bands: Readonly<Record<DifficultyBand, BandConfig>>;
  readonly hardConstraints: Readonly<Record<DifficultyBand, HardConstraints>>;
  readonly scoring: ScoringWeights;
  /** Difficulty modulation across the campaign, applied cyclically (permille of a band step). */
  readonly progressionWave: readonly number[];
  /** Every Nth level is an intentional breather. */
  readonly breatherInterval: number;
  /** Every Nth level showcases a silhouette rather than pushing difficulty. */
  readonly showcaseInterval: number;
  /** How many previous levels are considered when avoiding stencil repetition. */
  readonly stencilRecencyWindow: number;
  readonly maxArrowsPerLevel: number;
  readonly maxOccupiedCells: number;
}

const BANDS: Record<DifficultyBand, BandConfig> = {
  tutorial: {
    band: 'tutorial',
    minWidth: 5,
    maxWidth: 8,
    minHeight: 7,
    maxHeight: 10,
    occupancyPermille: [300, 400],
    initialMoveRatioPermille: [200, 1000],
    averageMoveRatioPermille: [200, 1000],
    arrowLengthRange: [2, 3],
    straightBias: 6,
    turnBias: 2,
    blockingBias: [0, 2],
    forcedStepRatioPermille: 100,
    dependencyDepthRange: [2, 4],
    candidateBudget: 8,
    maxPlacementAttempts: 400,
    maxRepairIterations: 2,
    archetypes: ['broad-branching'],
    strategies: ['uniform'],
    stencilCategories: ['shape'],
    maximumStencilComplexity: 2,
    minimumStencilSolidity: 0.5,
  },
  gentle: {
    band: 'gentle',
    minWidth: 10,
    maxWidth: 13,
    minHeight: 14,
    maxHeight: 18,
    occupancyPermille: [700, 800],
    initialMoveRatioPermille: [350, 550],
    averageMoveRatioPermille: [200, 340],
    arrowLengthRange: [2, 5],
    straightBias: 5,
    turnBias: 3,
    blockingBias: [4, 8],
    forcedStepRatioPermille: 120,
    dependencyDepthRange: [4, 7],
    candidateBudget: 10,
    maxPlacementAttempts: 4000,
    maxRepairIterations: 6,
    archetypes: ['broad-branching', 'sparse-center', 'silhouette-showcase'],
    strategies: ['uniform', 'center-out', 'frontier'],
    stencilCategories: ['shape', 'icon'],
    maximumStencilComplexity: 3,
    minimumStencilSolidity: 0.5,
  },
  easy: {
    band: 'easy',
    minWidth: 15,
    maxWidth: 19,
    minHeight: 20,
    maxHeight: 26,
    occupancyPermille: [650, 760],
    initialMoveRatioPermille: [350, 550],
    averageMoveRatioPermille: [200, 330],
    arrowLengthRange: [3, 8],
    straightBias: 4,
    turnBias: 3,
    blockingBias: [8, 14],
    forcedStepRatioPermille: 120,
    dependencyDepthRange: [5, 8],
    candidateBudget: 12,
    maxPlacementAttempts: 6000,
    maxRepairIterations: 6,
    archetypes: [
      'broad-branching',
      'island-regions',
      'sparse-center',
      'perimeter',
      'silhouette-showcase',
    ],
    strategies: ['uniform', 'frontier', 'center-out', 'perimeter-in', 'sparse-islands'],
    stencilCategories: ['shape', 'icon', 'glyph'],
    maximumStencilComplexity: 4,
    minimumStencilSolidity: 0.55,
  },
  medium: {
    band: 'medium',
    minWidth: 20,
    maxWidth: 25,
    minHeight: 26,
    maxHeight: 33,
    occupancyPermille: [620, 740],
    initialMoveRatioPermille: [330, 520],
    averageMoveRatioPermille: [180, 300],
    arrowLengthRange: [4, 11],
    straightBias: 4,
    turnBias: 4,
    blockingBias: [12, 20],
    forcedStepRatioPermille: 110,
    dependencyDepthRange: [6, 9],
    candidateBudget: 14,
    maxPlacementAttempts: 12000,
    maxRepairIterations: 6,
    archetypes: [
      'broad-branching',
      'narrow-chain',
      'island-regions',
      'dense-center',
      'corridor',
      'near-symmetric',
      'asymmetric',
      'silhouette-showcase',
    ],
    strategies: [
      'uniform',
      'frontier',
      'center-out',
      'perimeter-in',
      'corridor',
      'component-aware',
      'depth-biased',
      'near-symmetric',
    ],
    stencilCategories: ['shape', 'icon', 'glyph'],
    maximumStencilComplexity: 5,
    minimumStencilSolidity: 0.55,
  },
  hard: {
    band: 'hard',
    minWidth: 26,
    maxWidth: 32,
    minHeight: 34,
    maxHeight: 43,
    occupancyPermille: [570, 700],
    initialMoveRatioPermille: [320, 500],
    averageMoveRatioPermille: [160, 270],
    arrowLengthRange: [5, 15],
    straightBias: 3,
    turnBias: 4,
    blockingBias: [18, 28],
    forcedStepRatioPermille: 100,
    dependencyDepthRange: [7, 11],
    candidateBudget: 16,
    maxPlacementAttempts: 24000,
    maxRepairIterations: 6,
    archetypes: [
      'narrow-chain',
      'dense-center',
      'corridor',
      'island-regions',
      'near-symmetric',
      'asymmetric',
      'broad-branching',
    ],
    strategies: [
      'frontier',
      'center-out',
      'perimeter-in',
      'corridor',
      'component-aware',
      'depth-biased',
      'near-symmetric',
      'uniform',
    ],
    stencilCategories: ['shape', 'icon', 'glyph'],
    maximumStencilComplexity: 5,
    minimumStencilSolidity: 0.55,
  },
  expert: {
    band: 'expert',
    minWidth: 32,
    maxWidth: 40,
    minHeight: 42,
    maxHeight: 54,
    occupancyPermille: [490, 630],
    initialMoveRatioPermille: [310, 490],
    averageMoveRatioPermille: [150, 260],
    arrowLengthRange: [6, 19],
    straightBias: 3,
    turnBias: 5,
    blockingBias: [24, 40],
    forcedStepRatioPermille: 100,
    dependencyDepthRange: [8, 13],
    candidateBudget: 18,
    maxPlacementAttempts: 32000,
    maxRepairIterations: 6,
    archetypes: ['narrow-chain', 'dense-center', 'corridor', 'asymmetric', 'island-regions'],
    strategies: ['frontier', 'corridor', 'depth-biased', 'component-aware', 'center-out'],
    stencilCategories: ['shape', 'icon', 'glyph'],
    maximumStencilComplexity: 5,
    minimumStencilSolidity: 0.55,
  },
};

const HARD_CONSTRAINTS: Record<DifficultyBand, HardConstraints> = {
  tutorial: {
    minInitialMoves: 2,
    maxInitialMoves: 60,
    maxInitialMoveRatioPermille: 1000,
    minArrowCount: 2,
    minOccupiedCells: 3,
    minDependencyDepth: 1,
    maxForcedStepRatioPermille: 700,
    maxOccupancyDeviationPermille: 260,
    minSilhouetteCoverage: 0.3,
    maxRepetitionPenalty: 0.95,
    maxDirectionalImbalance: 0.8,
  },
  gentle: {
    minInitialMoves: 3,
    maxInitialMoves: 120,
    maxInitialMoveRatioPermille: 700,
    minArrowCount: 12,
    minOccupiedCells: 40,
    minDependencyDepth: 3,
    maxForcedStepRatioPermille: 500,
    maxOccupancyDeviationPermille: 220,
    minSilhouetteCoverage: 0.42,
    maxRepetitionPenalty: 0.75,
    maxDirectionalImbalance: 0.6,
  },
  easy: {
    minInitialMoves: 3,
    maxInitialMoves: 160,
    maxInitialMoveRatioPermille: 680,
    minArrowCount: 16,
    minOccupiedCells: 90,
    minDependencyDepth: 3,
    maxForcedStepRatioPermille: 450,
    maxOccupancyDeviationPermille: 220,
    minSilhouetteCoverage: 0.45,
    maxRepetitionPenalty: 0.75,
    maxDirectionalImbalance: 0.58,
  },
  medium: {
    minInitialMoves: 2,
    maxInitialMoves: 220,
    maxInitialMoveRatioPermille: 660,
    minArrowCount: 20,
    minOccupiedCells: 170,
    minDependencyDepth: 4,
    maxForcedStepRatioPermille: 420,
    maxOccupancyDeviationPermille: 220,
    minSilhouetteCoverage: 0.52,
    maxRepetitionPenalty: 0.78,
    maxDirectionalImbalance: 0.55,
  },
  hard: {
    minInitialMoves: 2,
    maxInitialMoves: 300,
    maxInitialMoveRatioPermille: 640,
    minArrowCount: 28,
    minOccupiedCells: 280,
    minDependencyDepth: 5,
    maxForcedStepRatioPermille: 400,
    maxOccupancyDeviationPermille: 220,
    minSilhouetteCoverage: 0.55,
    maxRepetitionPenalty: 0.8,
    maxDirectionalImbalance: 0.52,
  },
  expert: {
    minInitialMoves: 2,
    maxInitialMoves: 400,
    maxInitialMoveRatioPermille: 620,
    minArrowCount: 36,
    minOccupiedCells: 400,
    minDependencyDepth: 6,
    maxForcedStepRatioPermille: 400,
    maxOccupancyDeviationPermille: 220,
    minSilhouetteCoverage: 0.58,
    maxRepetitionPenalty: 0.8,
    maxDirectionalImbalance: 0.5,
  },
};

export const TUNING_V1: TuningConfig = {
  contentVersion: 1,
  bands: BANDS,
  hardConstraints: HARD_CONSTRAINTS,
  scoring: {
    occupancy: 0.18,
    initialMoves: 0.14,
    meanMoves: 0.1,
    forcedRatio: 0.12,
    dependencyDepth: 0.14,
    dispersion: 0.06,
    coverage: 0.12,
    repetition: 0.08,
    directionBalance: 0.06,
    unlockRhythm: 0.06,
    arrowLength: 0.1,
  },
  progressionWave: [0, 120, 260, 360, 260, 120, 0, -140],
  breatherInterval: 7,
  showcaseInterval: 25,
  stencilRecencyWindow: 9,
  maxArrowsPerLevel: 600,
  /** Ceiling on covered cells, which is what actually bounds render and solve cost. */
  maxOccupiedCells: 3600,
};

const TUNING_BY_VERSION: Record<number, TuningConfig> = {
  1: TUNING_V1,
};

/**
 * Older content versions keep their own tuning, so a level generated under content
 * version 1 keeps producing the same board even after version 2 ships.
 */
export function getTuning(contentVersion: number): TuningConfig {
  const tuning = TUNING_BY_VERSION[contentVersion];
  if (tuning === undefined) {
    throw new Error(`No tuning configuration for content version ${contentVersion}`);
  }
  return tuning;
}
